"use client";

import Text from "@/components/common/Typography/Text";
import CurrencyIcon from "@/components/icons/CurrencyIcon";
import FlightIconV2 from "@/components/icons/FlightIconV2";
import HomeIcon from "@/components/icons/HomeIcon";
import HotelsIconV2 from "@/components/icons/HotelsIconV2";
import MyBookingIcon from "@/components/icons/MyBookingIcon";
import PromotionIconV2 from "@/components/icons/PromotionIconV2";
import ToursIcon from "@/components/icons/ToursIcon";
import VietnamIcon from "@/components/icons/VietnameIcon";
import ESimIcon from "@/components/icons/eSimIcon";
import { useIsDesktop } from "@/hooks/app/useIsDesktop";
import { useIsAuthen } from "@/hooks/auth/useIsAuthen";
import { useAppDispatch, useAppSelector } from "@/lib/redux/hooks";
import { logout } from "@/lib/redux/features/user/reduce";
import { removeCookie, removeLocalStorage } from "@/utils/app/storage";
import { Avatar, Button, Col, Drawer, List, Row } from "antd";
import Link from "@/components/common/LinkNext";
import { useRouter } from "next/navigation";
import { globalActions } from "@/lib/redux/features/global/reduce";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faBlog, faPassport } from "@fortawesome/free-solid-svg-icons";
import { useTERouter } from "@/hooks/app/useTERouter";
import { useTranslation } from "react-i18next";
import _ from "lodash";
import { useBlogCategories } from "@/hooks/blog/useBlogCategories";
import { getCategoryLabel } from "@/utils/blog/getCategoryLabel";

const NavigationList = () => {
  const router = useTERouter();
  const dispatch = useAppDispatch();
  const shownMobileNav = useAppSelector((state) => state.global.shownMobileNav);
  const isDesktop = useIsDesktop();
  const isAuthen = useIsAuthen();
  const userData = useAppSelector((state) => state.user.profile?.data);
  const { t, i18n } = useTranslation()
  const { categories: exploreCategories, categoryNameMap } = useBlogCategories()

  const handleLogout = () => {
    dispatch(logout());
    removeLocalStorage("user");
    removeCookie("token");
    router.push("/");
    window.location.reload();
  };

  const handleProfileClick = () => {
    dispatch(globalActions.toggleMobileNav());
    router.push("/profile");
  };

  // Handler for "Đặt chỗ của tôi" navigation
  const handleMyBookingClick = () => {
    // First close the menu
    dispatch(globalActions.toggleMobileNav());

    // Then navigate to the appropriate page
    if (isAuthen) {
      router.push("/profile?tab=my_booking");
    } else {
      router.push("/dat-cho-cua-toi");
    }
  };

  // Handler for promotions page
  const handlePromotionsClick = () => {
    dispatch(globalActions.toggleMobileNav());
    router.push("/khuyen-mai");
  };

  // Handler for my vouchers page
  const handleMyVouchersClick = () => {
    dispatch(globalActions.toggleMobileNav());
    router.push("/my-vouchers");
  };

  // Generic navigation handler to ensure menu closes before navigation
  const handleNavigate = (path: string) => {
    dispatch(globalActions.toggleMobileNav());
    router.push(path);
  };

  if (isDesktop) {
    return <></>;
  }

  return (
    <Drawer
      styles={{
        header: {
          display: "none",
        },
        mask: {
          top: "6rem",
        },
        wrapper: {
          top: "6rem",
          boxShadow: "none",
        },
      }}
      zIndex={2000}
      classNames={{
        wrapper: `layout__mobilenav${shownMobileNav ? " open" : ""}`,
        mask: "layout__mobilenav--mask",
      }}
      onClose={() => dispatch(globalActions.toggleMobileNav())}
      open={shownMobileNav}
      size={"100%"}
      footer={
        isAuthen ? (
          <Row
            align="middle"
            gutter={[15, 15]}
            className="layout__mobilenav--profile"
          >
            <Col onClick={handleProfileClick} style={{ cursor: "pointer" }}>
              <Avatar size={40}>{userData?.firstName?.charAt(0)}</Avatar>
            </Col>
            <Col
              flex="auto"
              onClick={handleProfileClick}
              style={{ cursor: "pointer" }}
            >
              <Row>
                <Text strong>
                  {userData?.lastName} {userData?.firstName}
                </Text>
              </Row>
              <Row>
                <Text className="rank rank__silver">{t("app.silver_rank")}</Text>
              </Row>
            </Col>
            <Col>
              <Button type="text" danger onClick={handleLogout}>
                {t("common.logout")}
              </Button>
            </Col>
          </Row>
        ) : (
          <Row gutter={[10, 10]}>
            <Col>
              <Link href="/login">
                <Button size="large" type="primary" ghost>
                  {t("common.login")}
                </Button>
              </Link>
            </Col>

            <Col>
              <Link href="/sign-up">
                <Button size="large" type="primary">
                  {t("common.register")}
                </Button>
              </Link>
            </Col>
          </Row>
        )
      }
    >
      <List
        split={false}
        dataSource={[
          {
            label: t("common.home"),
            icon: <HomeIcon />,
            onClick: () => handleNavigate("/"),
            href: "/"
          },
          {
            label: t("common.hotel"),
            icon: <HotelsIconV2 />,
            onClick: () => handleNavigate("/hotels"),
            href: "/hotels"
          },
          {
            label: t("common.flight"),
            icon: <FlightIconV2 />,
            onClick: () => handleNavigate("/ve-may-bay"),
            href: "/"
          },
          {
            label: "Tour",
            icon: <ToursIcon />,
            onClick: () => handleNavigate("/tours"),
            href: "/tours"
          },
          {
            label: "Visa",
            icon: <FontAwesomeIcon icon={faPassport} />,
            onClick: () => handleNavigate("/dich-vu-visa"),
            href: "/dich-vu-visa"
          },
          {
            label: "eSIM",
            icon: <ESimIcon />,
            onClick: () => handleNavigate("/esim-du-lich"),
            href: "/esim-du-lich"
          },
          {
            label: t("common.my_booking"),
            icon: <MyBookingIcon />,
            onClick: handleMyBookingClick,
            href: isAuthen ? "/profile?tab=my_booking" : "/dat-cho-cua-toi"
          },
          {
            label: t("common.explore"),
            icon: <FontAwesomeIcon icon={faBlog} />,
            subItems: exploreCategories.map((cat) => ({
              label: getCategoryLabel(cat, t, categoryNameMap),
              onClick: () => {
                dispatch(globalActions.toggleMobileNav());
                handleNavigate(`/explore?category=${cat}`);
              },
              href: `/explore?category=${cat}`
            })),
          },
          {
            label: t("common.promotion"),
            icon: <PromotionIconV2 />,
            onClick: handlePromotionsClick,
            href: "/khuyen-mai"
          },
          // Only show "Khuyến mãi của tôi" for authenticated users
          ...(isAuthen
            ? [
              {
                label: t("common.my_promotion"),
                icon: <PromotionIconV2 />,
                onClick: handleMyVouchersClick,
                href: "/my-vouchers"
              },
            ]
            : []),
          {
            label: `${t("common.country")} ${_.lowerFirst(t("common.and"))} ${_.lowerFirst(t("common.language"))}`,
            icon: i18n.language === "en" ? <img width={22} height={22} style={{ width: '2.2rem', height: '2.2rem' }} alt="american-flag-icon" src="/static/images/american-flag-icon.png" /> : <VietnamIcon />,
            extra: <Text strong>{i18n.language === "en" ? t("common.english") : t("common.vietnamese")}</Text>,
          },
          {
            label: t("common.currency"),
            icon: <CurrencyIcon />,
            extra: <Text strong>{i18n.language === "en" ? "USD" : 'VND'}</Text>,
          },
        ]}
        renderItem={({ label, icon, extra, href, onClick, subItems }) => {
          return (
            <List.Item
              onClick={() => {
                if (!subItems) {
                  // Don't toggle the mobile nav here, it's already handled in the individual onClick handlers
                  dispatch(globalActions.closeMobileSearchFlightForm());
                  onClick && onClick();
                }
              }}
            >
              <Row
                className="layout__mobilenav--item"
                align="middle"
                justify="space-between"
              >
                <Col>
                  <Row gutter={[10, 10]} wrap={false} align="middle">
                    <Col>{icon}</Col>
                    <Col>{label}</Col>
                  </Row>
                </Col>
                <Col>{extra}</Col>
              </Row>
              {subItems && (
                <div style={{ paddingLeft: "2rem", marginTop: "0.5rem" }}>
                  {subItems.map((subItem: any, index: number) => (
                    <div
                      key={index}
                      onClick={(e) => {
                        e.stopPropagation();
                        dispatch(globalActions.closeMobileSearchFlightForm());
                        dispatch(globalActions.toggleMobileNav());
                        subItem.onClick && subItem.onClick();
                      }}
                      style={{
                        padding: "0.75rem 0",
                        cursor: "pointer",
                        borderBottom:
                          index < subItems.length - 1
                            ? "1px solid #f0f0f0"
                            : "none",
                        transition: "all 0.3s ease",
                      }}
                      className="submenu-item"
                    >
                      <Text style={{ fontSize: "14px", color: "#666" }}>
                        {subItem.label}
                      </Text>
                    </div>
                  ))}
                </div>
              )}
            </List.Item>
          );
        }}
      />
    </Drawer>
  );
};

export default NavigationList;
